import React, { useState } from "react";
import styled, { css } from "styled-components";
import CaseStudiesCard from "../CaseStudiesCard";
import Rule from "../../../Rule";
import Heading from "../../../Heading";
import Image from "../../../Image";
import Box from "../../../Box";
import BlogLayout from "../BlogLayout";
import Stack from "../../../Stack";
import ProjectHeroImage from "../../Projects/ProjectHeroImage";
import ExpensesCover from "../../../../img/casestudy/mobile-design/expenses.png";
import ExpensesMappingExperiences from "../../../../img/casestudy/mobile-design/expenses-mapping-experiences.png";
import OnTheGoConcepts from "../../../../img/casestudy/mobile-design/on-the-go-concepts.png";
import OnTheGoHackday from "../../../../img/casestudy/mobile-design/on-the-go-hackday.jpg";
import CapturePosters from "../../../../img/casestudy/mobile-design/capture-posters.jpg";
import OnTheWristAndroidLocked from "../../../../img/OnTheWrist-Android-Locked.png";
import OnTheWristAndroid from "../../../../img/OnTheWrist-Android.png";
import Capture1 from "../../../../img/capturex-1.jpg";
import Capture2 from "../../../../img/capturex-2.jpg";
import OnTheWristVideo from "../../../../video/onTheWrist.mp4";

const ExpensesCard = styled(CaseStudiesCard)`
  p {
    max-width: 80ch;
  }
`;

const TwoUpGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--spacing-medium);
  @media (min-width: 700px) {
    gap: var(--spacing-large);
    grid-template-columns: 1fr 1fr;
  }
`;

const PortfolioFrameContainer = styled.div`
  width: 100%;
`;

const PortfolioFrameInner = styled.div`
  width: 100%;
  max-width: 70rem;
  margin: 0 auto;
  padding-top: var(--spacing-medium);
  padding-bottom: var(--spacing-medium);
  @media (min-width: 850px) {
    padding-top: var(--spacing-x-large);
    padding-bottom: var(--spacing-x-large);
  }
`;

const PortfolioFrameFigure = styled.figure`
  margin: 0;
  border-radius: var(--radius-card);
  border-radius: 24px;
  overflow: hidden;
  width: 100%;
  background: #2b2b2b;
`;

const PortfolioFrameCaption = styled.figcaption`
  background: var(--dark-grey-palette);
  color: var(--white-palette);
  padding: var(--spacing-medium);
  padding-top: calc(var(--spacing-medium) - 0.8rem);
  text-align: center;
  font-size: var(--font-size-text-small);
`;

const ModalButton = styled.button`
  margin: 0;
  background-color: rgba(0, 0, 0, 0.05);
  display: inline-block;
  text-align: center;
  color: white;
  height: auto;
  text-decoration: none;
  padding: var(--spacing-x-small) var(--spacing-small);
  border-radius: 3.125em;
  line-height: 1;
  box-shadow: 0 1px 1px 0px rgba(0, 0, 0, 0.5), 0 -1px 0 0 rgba(255, 255, 255, 0.1);
  border: none;
  font-size: var(--font-size-text-small);
  font-weight: 700;
  cursor: pointer;
  &:hover,
  &:focus {
    background-color: rgba(0, 0, 0, 0.3);
  }
`;

const Modal = styled.div`
  ${(props) =>
    props.open &&
    css`
      background: #000000cc;
      top: 0;
      left: 0;
      position: fixed;
      height: 100%;
      width: 100%;
      z-index: 100000;
      display: flex;
      justify-content: center;
      align-items: center;
      padding: var(--spacing-large);
    `}
`;

const Video = styled.video`
  width: 100%;
  max-width: 74rem;
  height: auto;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 24px;
  left: 24px;
  margin: 0;
  background-color: #333333;
  display: inline-block;
  text-align: center;
  color: white;
  height: auto;
  text-decoration: none;
  padding: 0px 1.5em;
  border-radius: 3.125em;
  line-height: 3;
  box-shadow: 0 1px 1px 0px rgba(0, 0, 0, 0.5), 0 -1px 0 0 rgba(255, 255, 255, 0.1);
  border: none;
  font-size: var(--font-size-text-small);
  cursor: pointer;
  &:hover,
  &:focus {
    color: #ff73df;
  }
`;

function Expenses() {
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <ExpensesCard id="expenses">
      <Heading
        variant="smaller"
        style={{ marginTop: 0, marginBottom: "var(--spacing-x-tiny)", textTransform: "uppercase" }}
      >
        Case Study
      </Heading>
      <p style={{ marginBottom: 0, fontSize: "var(--font-size-text-medium)" }}>
        Designing mobile experiences for expense capture and payment awareness across iOS and Wear OS.
      </p>
      <Rule spacing="medium" inverted mt="var(--spacing-medium)" mb="var(--spacing-large)" />

      <BlogLayout wide>
        <Stack spacing="var(--spacing-large)">
          <Box>
            <Heading variant="larger" style={{ marginBottom: 0 }}>
              Expenses
            </Heading>
            <p
              style={{
                fontSize: "var(--font-size-heading-small)",
                marginBottom: "var(--spacing-tiny)",
                maxWidth: "80ch",
              }}
            >
              Improving MYOB Essentials by making expense capture faster and more convenient, with mobile and wearable
              concepts that reduce day-to-day bookwork friction.
            </p>
          </Box>

          <TwoUpGrid>
            <Box>
              <Heading variant="small">Context</Heading>
              <p>
                MYOB Essentials is built on helping small businesses succeed through time-saving tools that reduce
                day-to-day bookwork. Customer feedback showed expense capture was not convenient enough in real moments
                of use.
              </p>
            </Box>
            <Box>
              <Heading variant="small">Opportunity</Heading>
              <p>
                This friction impacted satisfaction and risked churn as competitors offered stronger capture workflows.
                The opportunity was to improve expense capture so customers could stay organised, compliant, and more
                successful with MYOB Essentials. This phase represented early exploration of native capability
                directions at MYOB, directly shaped by customer feedback.
              </p>
            </Box>
          </TwoUpGrid>
          <Image dark src={ExpensesCover} alt="Expense capture and payment reminder concepts" caption="Insights" />

          <Box>
            <Heading variant="small">Journey Mapping</Heading>
            <p>
              We used journey mapping to share work early, surface assumptions, and clarify the real problem. Simple
              visuals made ideas concrete, exposed gaps, and kept design, product, and engineering aligned as direction
              evolved.
            </p>
            <p>
              A strong example was rural accounting, where mapping end-to-end flows aligned stakeholders on the real
              handoffs between business owners and accountants.
            </p>
          </Box>
          <Image
            dark
            src={ExpensesMappingExperiences}
            alt="Mapping experience flows for expense and payment awareness"
            caption="Journey mapping"
          />

          <TwoUpGrid>
            <Box>
              <Heading variant="small">Figma Concepts</Heading>
              <p>
                Quick Figma concepts explored low-friction capture patterns and clearer information hierarchy before
                moving into higher-fidelity directions.
              </p>
            </Box>
            <Box>
              <Heading variant="small">Exploration</Heading>
              <p>
                Variants were used to test entry points and interaction flow so decisions could be grounded in practical
                use, not assumptions.
              </p>
            </Box>
          </TwoUpGrid>
          <Image dark src={OnTheGoConcepts} alt="On-the-go concept explorations" caption="Figma Concepts" />

          <Box>
            <Stack spacing="var(--spacing-large)">
              <Box>
                <Box style={{ marginBottom: "var(--spacing-medium)" }}>
                  <Box
                    display
                    style={{
                      display: "grid",
                      gap: "var(--spacing-medium)",
                      gridTemplateColumns: "1fr auto",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginBottom: "var(--spacing-small)",
                    }}
                  >
                    <Heading variant="small" style={{ marginBottom: 0 }}>
                      Results: On The Wrist
                    </Heading>
                    <ModalButton onClick={() => setModalOpen(true)}>Hackday Video</ModalButton>
                  </Box>
                  <p style={{ marginBottom: 0 }}>
                    We ran two separate hackdays to test the concepts in context: <strong>On The Go</strong> focused on
                    fast expense capture in iOS, while <strong>On The Wrist</strong> focused on payment awareness in a
                    native Wear OS experience. Together, these sessions translated ideas into testable prototypes and
                    helped validate feasibility across both device types.
                  </p>
                </Box>

                <Image
                  dark
                  src={OnTheGoHackday}
                  alt="Hackday prototyping for on-the-go and on-the-wrist scenarios"
                  caption="On The Wrist - Android Wear"
                />
              </Box>
            <Box>
              <Heading variant="small" style={{ marginBottom: "var(--spacing-small)" }}>
                Rebrand Rework
              </Heading>
              <p>
                After the company rebrand, I revisited the Android Wear concept to see how the payment reminder
                experience could translate into the newer visual language. The rework focused on keeping the watch
                interaction glanceable while making the interface feel more aligned with the updated MYOB brand.
              </p>
              <PortfolioFrameContainer>
                <PortfolioFrameFigure>
                  <PortfolioFrameInner>
                    <ProjectHeroImage
                      srcWatch1={OnTheWristAndroidLocked}
                      srcWatch2={OnTheWristAndroid}
                      watchAndroid
                      noBottomMargin
                      lazy
                      alt="On The Wrist Android Wear concepts"
                    />
                  </PortfolioFrameInner>
                  <PortfolioFrameCaption>On The Wrist - Android Wear Frames</PortfolioFrameCaption>
                </PortfolioFrameFigure>
              </PortfolioFrameContainer>
              </Box>
              <Box>
                <Heading variant="small" style={{ marginBottom: "var(--spacing-small)" }}>
                  Results: On The Go
                </Heading>
                <p>
                  This exploration helped shape the reasoning behind later investment in native expense workflows. MYOB
                  then released Capture as a formal product stream with its own team and designers. I later contributed
                  to native invoicing capability work before moving into the MYOB Design System team, Feelix.
                </p>
                <Image
                  dark
                  src={CapturePosters}
                  alt="Capture and On The Wrist summary posters"
                  caption="On The Go - iOS & Android Hackday"
                />
              </Box>
              <Box>
                <Heading variant="small" style={{ marginBottom: "var(--spacing-small)" }}>
                  Rebrand Rework
                </Heading>
                <p>
                  I also reworked the mobile Capture screens after the rebrand, using the original exploration as a
                  base while updating the interface treatment. The goal was to keep the expense capture flow direct and
                  familiar, while showing how it could sit more comfortably inside the newer MYOB product direction.
                </p>
              <PortfolioFrameContainer>
                <PortfolioFrameFigure>
                  <PortfolioFrameInner>
                    <ProjectHeroImage
                      srcMobile1={Capture1}
                      srcMobile2={Capture2}
                      mobile
                      lazy
                      alt="Capture mobile app concepts"
                    />
                  </PortfolioFrameInner>
                  <PortfolioFrameCaption>Capture - Reworked Mobile Frames</PortfolioFrameCaption>
                </PortfolioFrameFigure>
              </PortfolioFrameContainer>
              </Box>
            </Stack>
          </Box>

          <Box>
            <Heading variant="small">Where This Led</Heading>
            <p>
              These early explorations into native capability at MYOB were driven by direct customer feedback and helped
              shape the broader direction of expense tooling. Capture later became a formal stream with its own team and
              designers, while I moved on to support native invoicing capability before joining the Feelix Design System
              team.
            </p>
          </Box>
        </Stack>
      </BlogLayout>
      {modalOpen ? (
        <Modal open>
          <CloseButton onClick={() => setModalOpen(false)}>Close</CloseButton>
          <Video controls>
            <source src={OnTheWristVideo} type="video/mp4" />
            Your browser does not support the video tag.
          </Video>
        </Modal>
      ) : null}
    </ExpensesCard>
  );
}

export default Expenses;
